import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import appDisplay from 'scoreboard/components/app-display'
import Dataset from '../model/dataset'
import DatasetPermissionsForm from '../dataset-permissions-form'
import DatasetsPrincipalPanel from './datasets-principal-panel'
import PermissionsByFilter from './permissions-by-filter'
import spotlight from 'scoreboard/components/spotlight'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import template from './permissions-panel.hbs'
import DatasetsTableExpander from './datasets-table-expander'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import user from 'scoreboard/components/user'
import permissionsUtil from './permissions-util'

/**
 * This view IS a principal panel, but it also contains 3 other principal panels
 * through the PermissionsByFilter view (Direct/Indirect/View All)
 *
 * @property {Dataset} model
 */
var PermissionsPanel = DatasetsPrincipalPanel.extend({

	events: _.defaults({
		'click .settings': 'settings'
	}, DatasetsPrincipalPanel.prototype.events),

	// see principal-panel.js
	_principalTableSelector: '.principal-table',

	/**
	 * Creates the Permissions panel on the Datasets Edit screen
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the Dataset
	 *
	 * @param {Array} options.currentPermissions array of current permissions
	 *
	 * @param {String} options.permissionBy 'principal' or 'filter' to set the
	 *     state of the toggle
	 */
	initialize: function(options = {}) {
		// Keep all sorting concerns right here
		this.options = options
		let bannerLinkMessage =
			`<a class="text-link sp-navigate" href="/admin/dataset-permissions-audit/${this.model.id}">` +
			i18n.get('admin.datasetPermissionsAudit') +
			'</a>'
		this.options.banner = i18n.get('datasets.advancedPermissionsBanner', bannerLinkMessage)
		options.currentPermissions = _.sortBy(options.currentPermissions,
				p => p.principal.label.toLowerCase())

		// Map the inactive flag to the permission to make rendering easier
		_.each(options.currentPermissions, p => p.principal.inactive = p.inactive)

		options.currentPrincipals = _.map(options.currentPermissions, 'principal')
		options.sort = false
		options.datasetLowercase = i18n.get('datasets.dataset').toLowerCase()
		DatasetsPrincipalPanel.prototype.initialize.apply(this, arguments)

		this.treeItemsById = _.keyBy(this.model.get('treeItems'), 'id')
		this.template = template // provide a custom template for the PrincipalPanel
		this.maxRows = options.maxRows
		// if a viewState is passed in, that means the panels and tables need to
		// created with specific collapsed states.
		this.viewState = options.viewState
	},

	render: function() {
		DatasetsPrincipalPanel.prototype.render.apply(this, arguments)
		this.setElement(this.el)
		this.principalTable = this.$(this._principalTableSelector)

		this._renderToggle()
		this._renderCustomToggle()
		this._renderFilterPanel()
		this._onChangeAdvancedPermission()
		this._setupDatasetsTableExpander()

		delete this.viewState

		return this
	},

	// This view is contained within a principal-panel, so we do not need to
	// setup tooltips again, it's covered by the containing principal-panel.
	_setupTooltips: function() {
		// Do nothing
	},

	// Setup the main 'by user or group' table for expanding/collapsing.
	_setupDatasetsTableExpander: function() {
		this.datasetsTableExpander &&  this.datasetsTableExpander.remove()
		this.datasetsTableExpander = new DatasetsTableExpander({
			table: this.principalTable,
			isCollapsed: _.result(this.viewState, 'principal.tableCollapsed'),
			maxRows: this.maxRows,
			scrollContainer: this.options.scrollContainer
		}).setup()
	},

	_renderToggle: function () {
		this.toggle = new LabeledToggle({
			options: [{
				value: 'principal',
				label: i18n.get('common.byWithArg',
					i18n.get('common.user.userOrGroup'))
			}, {
				value: 'filter',
				label: i18n.get('common.byWithArg',
					i18n.get('datasets.permissions.restrictiveFilter'))
			}],
			value: this.options.permissionBy || 'principal'
		})
		this.listenTo(this.toggle, 'change', this._onByToggle.bind(this))
		this.$('.panel-header').append(this.toggle.el)
		this.toggle.render()
	},

	_renderCustomToggle: function () {
		this.advancedPermissionsToggle = new OnOffToggle({
			name: 'advancedPermissionsToggle',
			checked: this.model.get('permissions'),
			label: i18n.get('datasets.permissions.advanced')
		})
		this.listenTo(this.advancedPermissionsToggle, 'change', this._onChangeAdvancedPermission.bind(this))
		this.$('.panel-header').append(this.advancedPermissionsToggle.el)
		this.advancedPermissionsToggle.render()
	},

	_renderFilterPanel: function () {

		this.permissionsByFilter = new PermissionsByFilter({
			model: this.model,
			treeItemsById: this.treeItemsById,
			principalDataRowFn: this.getPrincipalDataRow.bind(this),
			principalModelFn: (id, type, fieldId) =>
				this._locatePrincipalRow(id, type, fieldId).data(),
			viewState: _.get(this.viewState, 'permissionsByFilter'),
			maxRows: this.maxRows,
			scrollContainer: this.options.scrollContainer
		})
		this.listenForFilterChanges()
		this.$('.advanced-permissions').append(this.permissionsByFilter.el)
		this.permissionsByFilter.render()
	},

	listenForFilterChanges: function () {

		// Respond to changes in one of the filter permissions panels
		// This principal needs added first because it holds the model
		this.permissionsByFilter.on('before-add', principal => {
			const newRow = this.renderPrincipal(principal)
			this._selectTableContent('tr').last().after(newRow)
			this.afterPrincipalAttached(newRow, principal)
			this.trigger('change change:add', principal)
			_.invoke(this.principalChooser, 'excludePrincipal', principal)
		})

		this.permissionsByFilter.on('add:filter', filters => {
			this.trigger('change') // #35318 just dirty the form for now
		})

		this.permissionsByFilter.on('edit', e => this.settings(e))

		this.permissionsByFilter.on('delete', principal => {
			const modelRow = this.getPrincipalDataRow(principal)
			modelRow.remove()
			_.invoke(this.principalChooser, 'removePrincipal', principal)
			this.trigger('change change:delete', principal)
		})

		this.permissionsByFilter.on('beforeRenderPanels', state => {
			this.trigger('beforeRenderPanels', state)
		})

		// Be sure to re-render the principal-table which is not one of the
		// filter panel tables.
		this.permissionsByFilter.on('render:panels before-add', () => {
			this._setupDatasetsTableExpander()
		})
	},

	getViewState: function() {
		// Only this view must know about/access what is in this state object
		return {
			principal: {
				tableCollapsed: !this.principalTable.hasClass('is-expanded')
			},
			permissionsByFilter: this.permissionsByFilter.getViewState()
		}
	},

	settings: function(e) {
		const tr = this.$(e.target).closest('tr')
		const modelRow = this.getPrincipalDataRow(tr)
		this.principalSettingsDialog(this.model, this.treeItemsById, tr, modelRow)
	},

	addPrincipal: function(principal) {
		this.once('change:add', (principal, newRow) => {
			const {type, treeRootId, fieldId} = principal
			const filters = []
			if (type === 'rollup-tree-group') {
				filters.push(permissionsUtil.newBuiltInRollupFilter(
					principal,treeRootId, fieldId))
			}
			let permissions = _.extend({filters, principal}, newRow.data('permissions'))
			newRow.data('permissions', permissions)
			this.permissionsByFilter.syncPrincipal(principal, filters) // Keep principal panels in sync
			this._setupDatasetsTableExpander()
		})
		DatasetsPrincipalPanel.prototype.addPrincipal.apply(this, arguments)
	},

	'delete': function(e) {
		const principal = DatasetsPrincipalPanel.prototype.delete.apply(this, arguments)
		this.permissionsByFilter.deletePrincipal(principal) // Keep principal panels in sync
	},

	/**
	 * @param {JQuery|Object} trOrPrincipal Can be either a row with data
	 *     attributes, or a principal
	 */
	getPrincipalDataRow: function(trOrPrincipal) {
		const {id, type, fieldId} = _.result(trOrPrincipal, 'data', trOrPrincipal)
		return this._locatePrincipalRow(id, type, fieldId)
	},

	_locatePrincipalRow: function(id, type, fieldId) {
		let selector = `tr[data-id="${id}"][data-type="${type}"]`
		if (fieldId) {
			selector += `[data-field-id="${fieldId}"]`
		}
		selector += `.principal-item`
		return this._selectTableContent(selector)
	},

	principalSettingsDialog: function (model, treeItemsById, row, modelRow) {
		// Copy the `.principal-name` so we can make changes to it without affecting the original.
		let principalName = row.find('.principal-name').clone()
		let principalGroupType = principalName.find('.post-label').remove().text()
		const permissions = modelRow.data('permissions')
		const {principal} = permissions

		const createPermissionForm = () => {
			const datasetPermissions = new DatasetPermissionsForm({
				model,
				permissionsLabel: principal.label,
				permissionsGroupType: _.trim(principalGroupType),
				permissions,
				treeItemsById
			})
			datasetPermissions.on('done', permissions => {
				// If editing permissions by-filter, the model row is different
				modelRow.data('permissions', permissions)
				const {principal, filters} = permissions
				const rollupValue = Dataset.getBuiltInValueFromFilters(principal, filters)
				this.syncRollupOption(modelRow, rollupValue)
				this.trigger('change')
				this.permissionsByFilter.reloadPermission(permissions)
			})
			return datasetPermissions
		}

		let dialog
		if (principal.type === 'user') {
			const url = `/api/datasets/${model.id}/users/${principal.id}`
			dialog = appDisplay.dialog($.get(url), ({readOnly}) => {
				principal.readOnly = readOnly
				return createPermissionForm()
			})
		} else { // group or tree template
			dialog = appDisplay.dialog(createPermissionForm())
		}
		// Wait until the dialog is completely closed before highlighting the edited row
		// (could be a dialog OR a promise for a dialog)
		Promise.resolve(dialog).then(d => d.on('close', () => spotlight(row)))
	},

	getPermissions: function() {
		var permissions = []
		const advanced = this.advancedPermissionsToggle.val()
		if (!advanced) {
			// Advanced permissions should be retained on the client until the
			// form is submitted, but once they save we wipe them out.
			return permissions
		}
		const principalRows = this._selectTableContent('tbody tr.principal-item')
		_.each(principalRows, (row, index)  => {
			// Don't read principal attributes from the dom, we keep them up to date ourselves
			// When changes are made; see addPrincipal() above
			const {fieldId, permissions: p = {}} = $(row).data()
			p.fieldId = p.principal.fieldId || fieldId
			permissions.push(p)
		})
		return permissions
	},

	/**
	 * The toggle that shows after advanced permissions are turned on.
	 * (By User/Group | By Permission Filter)
	 *
	 * @param isAdvancedPermission If advanced permissions are toggled on.
	 * If they're not all those components must be hidden
	 *
	 * @private
	 */
	_onByToggle: function(isAdvancedPermission) {
		if (isAdvancedPermission) {
			this.permissionBy = this.toggle.val()
			const byPrincipal = this.permissionBy === 'principal'
			this.principalTable.toggleClass('is-not-displayed', !byPrincipal)
			this.permissionsByFilter.$el.toggleClass('is-not-displayed', byPrincipal)
			if (!byPrincipal) {
				this.permissionsByFilter.showDirectPermissions()
			}
		} else {
			this.principalTable.addClass('is-not-displayed')
			this.permissionsByFilter.$el.addClass('is-not-displayed')
		}
		this.permissionsByFilter.resizePanels()
		this.resize()
	},

	/**
	 * Updates view based on state of the "Advanced Permission" toggle
	 * (upper-right corner)
	 */
	_onChangeAdvancedPermission: function() {
		const advanced = this.advancedPermissionsToggle.val()
		this.toggle.$el.toggleClass('is-not-displayed', !advanced)
		this.$('.advanced-permissions-banner').toggleClass('is-not-displayed', !advanced)
		this.$('.panel-header-large').toggleClass('has-labeled-toggle', advanced)
		toggleAdvancedPermission(this, this)
		this._onByToggle(advanced)
		return advanced
	},

	remove: function() {
		DatasetsPrincipalPanel.prototype.remove.apply(this, arguments)
		_.invokeMap([this.toggle, this.permissionsByFilter,
			this.datasetsTableExpander, this.advancedPermissionsToggle], 'remove')
	}

})

/**
 * To be called when a user toggles to advanced permissions and is responsible
 * for updating the view and ensuring their principal exists in the list.
 *
 * To use this a naming convention is required:
 * - An element with the `.org-permissions` class will be displayed with the
 * toggle is disabled
 * - An element with the `.advanced-permissions` class will be displayed with
 * the toggle is enabled
 *
 * @param {Backbone.View} view The view that has the .org-permissions
 * and .advanced-permissions elements
 *
 * @param {PrincipalPanel} principalPanel A principal panel object that will
 * be used to retrieve and possibly add the current principal to.
 */
function toggleAdvancedPermission(view, principalPanel) {
	const advanced = view.advancedPermissionsToggle.val()
	view.$('.org-permissions').toggleClass('is-not-displayed', advanced)
	view.$('.advanced-permissions').toggleClass('is-not-displayed', !advanced)
	if (advanced) {
		const currentPrincipals = principalPanel.getPrincipals() || []
		if (_.isEmpty(currentPrincipals)) {
			// #15952 #16343 if "Advanced Permissions" is enabled for the 1st
			// time make sure the current user is added so that they have access
			// to the dataset
			principalPanel.once('change:add', (principal, row) => {
				row.data('permissions', {
					administer: true,
					modifyPermissions: true,
					modifyRecords: true,
					viewRecords: true,
					viewRecordsTab: true,
					modifyViews: true
				})
			})
			principalPanel.addPrincipal({
				id: user.model.id,
				label: user.model.get('label'),
				type: 'user'
			})
		}
	}
	return advanced
}

export default PermissionsPanel
export { toggleAdvancedPermission }

PermissionsPanel.__filename__ = 'datasets/edit/permissions-panel.js'
